import React, { useContext } from 'react';
import { includes } from 'lodash-es';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import { Permission } from '@shared/permissions';
import { usePermissions } from '@shared/permissions/usePermissions';

interface IComponent {
  children: React.ReactNode;
  /**
   * Required permissions for this component.
   * User must have at least ONE of these permissions to see the component.
   * Use this for the new permission-based access control.
   */
  requiredPermissions?: Permission[];
  /**
   * If true, user must have ALL required permissions (not just one).
   * Default is false (user needs any one of the permissions).
   */
  requireAll?: boolean;
  /**
   * @deprecated Use `requiredPermissions` instead.
   * Legacy whitelist of email addresses.
   * Kept for backwards compatibility during migration.
   */
  whitelist?: string[];
  /**
   * @deprecated Will be removed in future version.
   * Legacy blacklist of email addresses.
   * Kept for backwards compatibility during migration.
   */
  blacklist?: string[];
}

/**
 * ScopedComponent - Conditionally renders children based on user permissions.
 * 
 * This component supports both the new permission-based system and the legacy
 * whitelist/blacklist pattern for backwards compatibility during migration.
 * 
 * @example New permission-based usage:
 * ```tsx
 * <ScopedComponent requiredPermissions={['orders.edit', 'orders.delete']}>
 *   <DeleteButton />
 * </ScopedComponent>
 * 
 * // Require ALL permissions:
 * <ScopedComponent requiredPermissions={['orders.edit', 'orders.delete']} requireAll>
 *   <DangerousAction />
 * </ScopedComponent>
 * ```
 * 
 * @example Legacy whitelist usage (deprecated):
 * ```tsx
 * <ScopedComponent whitelist={['admin@example.com']}>
 *   <AdminPanel />
 * </ScopedComponent>
 * ```
 * 
 * Priority:
 * 1. If `requiredPermissions` is provided, use permission-based checking
 * 2. Otherwise, fall back to legacy whitelist/blacklist behaviour
 * 3. If neither is provided, component is visible to all authenticated users
 */
const ScopedComponent = ({
  children,
  requiredPermissions,
  requireAll = false,
  whitelist = [],
  blacklist = [],
}: IComponent) => {
  const { currentUser } = useContext(AuthContext);
  const { hasPermission, hasAllPermissions } = usePermissions();

  // If using the new permission system
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasPermission(requiredPermissions);

    // Still respect blacklist even with new permission system
    const userIsBlacklisted = includes(blacklist, currentUser?.email);

    return (
      <>
        {hasAccess && !userIsBlacklisted && children}
      </>
    );
  }

  // Legacy whitelist/blacklist behaviour
  /*
    If `whitelist` is not passed to the ScopedComponent, it is effectively an
    implicit whitelist for anyone. This is typically used in the case that
    you want to explicitly blacklist someone without whitelisting everyone
    else at the same time.

    Blacklisting is explicit-only, so user emails must be added to blacklist
    someone from a component.
   */
  const userIsWhitelisted = whitelist.length === 0 || includes(whitelist, currentUser?.email);
  const userIsBlacklisted = includes(blacklist, currentUser?.email);

  return (
    <>
      {(userIsWhitelisted && !userIsBlacklisted) && children}
    </>
  );
};

export default ScopedComponent;
